import type { CSSProperties, ElementType, ReactElement, ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';
import './scroll-text.css';

/** Pixels per second the overflow scrolls at; clamped to a sane duration. */
const SPEED = 60;

/**
 * Single-line text that ellipsizes at rest and, on hover, scrolls through its
 * hidden tail and back. Measures the overflow on enter, so it works inside any
 * layout (flex captions, grid cells, block rows) without container units.
 * Text that fits never moves.
 */
export function ScrollText({
  as: Tag = 'span',
  className,
  children,
  title,
}: {
  as?: ElementType;
  className?: string;
  children: ReactNode;
  title?: string;
}): ReactElement {
  const ref = useRef<HTMLElement>(null);
  const [distance, setDistance] = useState(0);

  const onEnter = useCallback(() => {
    const element = ref.current;
    if (!element) return;
    setDistance(Math.max(0, element.scrollWidth - element.clientWidth));
  }, []);
  const onLeave = useCallback(() => setDistance(0), []);

  const active = distance > 0;
  const style = active
    ? ({
        '--scroll-text-distance': `-${distance}px`,
        '--scroll-text-duration': `${Math.min(12, Math.max(1.6, distance / SPEED))}s`,
      } as CSSProperties)
    : undefined;

  return (
    <Tag
      ref={ref}
      className={`scroll-text${active ? ' scroll-text--active' : ''}${className ? ` ${className}` : ''}`}
      style={style}
      title={title}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <span className="scroll-text__inner">{children}</span>
    </Tag>
  );
}
